import { useState, useEffect } from 'react';
import { Wrapper, ButtonSection, SectionTitle, ButtonGrid, ActionButton } from "../style-comps/AdminAttention.styles";
import { infoBoxStyle, inputStyle, labelStyle } from "../style-comps/AdminMaintenance.styles";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
    enableMaintenance,
    disableMaintenance,
    getMaintenanceStatus,
    updateRecoveryTime,
    invalidateTokens,
} from "../services/api";

const MySwal = withReactContent(Swal);

// 台北時間輸入 → UTC ISO string 送後端
const toUtcISOString = (localDateTimeStr) => {
    if (!localDateTimeStr) return null;
    return new Date(localDateTimeStr).toISOString();
};

const AdminMaintenance = () => {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [enableMessage, setEnableMessage] = useState('');
    const [enableRecoveryTime, setEnableRecoveryTime] = useState('');
    const [disableMessage, setDisableMessage] = useState('');
    const [recoveryTimeInput, setRecoveryTimeInput] = useState('');

    useEffect(() => {
        fetchStatus();
    }, []);

    const fetchStatus = async () => {
        setLoading(true);
        try {
            const data = await getMaintenanceStatus();
            setStatus(data);
        } catch (err) {
            console.error('取得維護狀態失敗:', err);
        } finally {
            setLoading(false);
        }
    };

    // 後台顯示台北時間
    const formatUtc = (utcStr) => {
        if (!utcStr) return '—';

        let dateString = utcStr;

        if (
            utcStr.includes('T') &&
            !utcStr.includes('Z') &&
            !utcStr.includes('+')
        ) {
            dateString = utcStr + 'Z';
        }

        const date = new Date(dateString);

        return date.toLocaleString('zh-TW', {
            timeZone: 'Asia/Taipei',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }) + ' (Taipei)';
    };

    const handleEnable = async () => {
        const result = await MySwal.fire({
            title: '開啟維護模式',
            text: '開啟後所有使用者將無法登入，確定要執行嗎？',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#61cbc9',
            cancelButtonColor: '#d33',
            confirmButtonText: '確定開啟',
            cancelButtonText: '取消',
        });
        if (!result.isConfirmed) return;

        try {
            const recoveryUtc = enableRecoveryTime ? toUtcISOString(enableRecoveryTime) : null;
            console.log('送出的 recoveryTimeUtc:', recoveryUtc);
            await enableMaintenance({
                message: enableMessage || '',
                recoveryTimeUtc: recoveryUtc,
            });
            await MySwal.fire({ title: '維護模式已開啟', icon: 'success', timer: 1500, showConfirmButton: false });
            setEnableMessage('');
            setEnableRecoveryTime('');
            fetchStatus();
        } catch (err) {
            MySwal.fire({ title: '執行失敗', text: err?.response?.data?.message || err.message, icon: 'error' });
        }
    };

    const handleDisable = async () => {
        const result = await MySwal.fire({
            title: '關閉維護模式',
            text: '關閉後系統恢復正常，確定要執行嗎？',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#61cbc9',
            cancelButtonColor: '#d33',
            confirmButtonText: '確定關閉',
            cancelButtonText: '取消',
        });
        if (!result.isConfirmed) return;

        try {
            await disableMaintenance({ message: disableMessage || '' });
            await MySwal.fire({ title: '維護模式已關閉', icon: 'success', timer: 1500, showConfirmButton: false });
            setDisableMessage('');
            fetchStatus();
        } catch (err) {
            MySwal.fire({ title: '執行失敗', text: err?.response?.data?.message || err.message, icon: 'error' });
        }
    };

    const handleUpdateRecoveryTime = async () => {
        if (!recoveryTimeInput) {
            MySwal.fire({ title: '請先選擇時間', icon: 'warning' });
            return;
        }
        const result = await MySwal.fire({
            title: '更新預計恢復時間',
            text: `將恢復時間設為 ${new Date(recoveryTimeInput).toLocaleString()}，確定嗎？`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#61cbc9',
            cancelButtonColor: '#d33',
            confirmButtonText: '確定',
            cancelButtonText: '取消',
        });
        if (!result.isConfirmed) return;

        try {
            await updateRecoveryTime({ recoveryTimeUtc: toUtcISOString(recoveryTimeInput) });
            await MySwal.fire({ title: '恢復時間已更新', icon: 'success', timer: 1500, showConfirmButton: false });
            setRecoveryTimeInput('');
            fetchStatus();
        } catch (err) {
            MySwal.fire({ title: '執行失敗', text: err?.response?.data?.message || err.message, icon: 'error' });
        }
    };

    const handleInvalidateTokens = async () => {
        const result = await MySwal.fire({
            title: '強制舊 JWT 失效',
            text: '這將強制所有使用者重新登入，確定要執行嗎？',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#61cbc9',
            cancelButtonColor: '#d33',
            confirmButtonText: '確定執行',
            cancelButtonText: '取消',
        });
        if (!result.isConfirmed) return;

        try {
            await invalidateTokens();
            await MySwal.fire({ title: '已強制所有舊 JWT 失效，即將重新登入', icon: 'success', timer: 2000, showConfirmButton: false });
            localStorage.removeItem('authToken');
            localStorage.removeItem('tokenExpiration');
            localStorage.removeItem('roles');
            localStorage.removeItem('authData');
            window.location.href = '/superadmin';
        } catch (err) {
            MySwal.fire({ title: '執行失敗', text: err?.response?.data?.message || err.message, icon: 'error' });
        }
    };

    const isOn = status?.IsEnabled === true;

    return (
        <Wrapper>
            <ButtonSection>

                {/* ── 系統資訊 ── */}
                <div>
                    <SectionTitle>系統資訊</SectionTitle>
                    <ButtonGrid>
                        {loading ? (
                            <p style={{ fontSize: '13px', color: '#888' }}>載入中...</p>
                        ) : status ? (
                            <div style={infoBoxStyle}>
                                <div>
                                    維護模式：
                                    <strong style={{ color: isOn ? '#e17055' : '#00b894' }}>
                                        {isOn ? '🔴 開啟中' : '🟢 關閉中'}
                                    </strong>
                                </div>
                                {status.Message && <div>維護訊息：<strong>{status.Message}</strong></div>}
                                <div>App Version：<strong>{status.AppVersion || '—'}</strong></div>
                                <div>API Version：<strong>{status.ApiVersion || '—'}</strong></div>
                                <div>預計恢復時間：<strong>{formatUtc(status.RecoveryTimeUtc)}</strong></div>
                                <div>Token 有效期起點：<strong>{formatUtc(status.TokenValidAfterUtc)}</strong></div>
                                <div>上次更新：<strong>{formatUtc(status.UpdatedAtUtc)}</strong></div>
                            </div>
                        ) : (
                            <p style={{ fontSize: '13px', color: '#e17055' }}>無法取得狀態</p>
                        )}
                        <ActionButton type="debug" onClick={fetchStatus}>
                            重新整理狀態
                        </ActionButton>
                    </ButtonGrid>
                </div>

                {/* ── 開啟維護 ── */}
                <div>
                    <SectionTitle>開啟維護模式</SectionTitle>
                    <ButtonGrid>
                        <div>
                            <label style={labelStyle}>維護訊息（選填）</label>
                            <input
                                type="text"
                                placeholder="例：系統維護中，預計 2 小時後恢復"
                                value={enableMessage}
                                onChange={(e) => setEnableMessage(e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>預計恢復時間（選填）</label>
                            <input
                                type="datetime-local"
                                value={enableRecoveryTime}
                                onChange={(e) => setEnableRecoveryTime(e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <ActionButton type="danger" onClick={handleEnable}>
                            開啟維護模式
                        </ActionButton>
                    </ButtonGrid>
                </div>

                {/* ── 關閉維護 ── */}
                <div>
                    <SectionTitle>關閉維護模式</SectionTitle>
                    <ButtonGrid>
                        <div>
                            <label style={labelStyle}>關閉訊息（選填）</label>
                            <input
                                type="text"
                                placeholder="例：系統已恢復正常"
                                value={disableMessage}
                                onChange={(e) => setDisableMessage(e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <ActionButton type="init" onClick={handleDisable}>
                            關閉維護模式
                        </ActionButton>
                    </ButtonGrid>
                </div>

                {/* ── 更新恢復時間 ── */}
                <div>
                    <SectionTitle>更新預計恢復時間</SectionTitle>
                    <ButtonGrid>
                        <div>
                            <label style={labelStyle}>新的預計恢復時間</label>
                            <input
                                type="datetime-local"
                                value={recoveryTimeInput}
                                onChange={(e) => setRecoveryTimeInput(e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <ActionButton type="debug" onClick={handleUpdateRecoveryTime}>
                            更新恢復時間
                        </ActionButton>
                    </ButtonGrid>
                </div>

                {/* ── Token 管理 ── */}
                <div>
                    <SectionTitle>Token 管理</SectionTitle>
                    <ButtonGrid>
                        <ActionButton type="danger" onClick={handleInvalidateTokens}>
                            部署後強制舊 JWT 失效
                        </ActionButton>
                    </ButtonGrid>
                </div>

            </ButtonSection>
        </Wrapper>
    );
};

export default AdminMaintenance;